'use client';

import { cn } from '@components/lib/utils';
import { useExplorer } from '@/hooks/useExplorer';
import { useCodeGenerationStore } from '@/stores/useCodeGenerationStore';
import { CircleArrowUpIcon } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import SDKSelector from './SDKSelector';

interface ConversationBottomInputProps {
  onSubmit: (prompt: string, sdk?: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
}

const ConversationBottomInput = ({
  onSubmit,
  placeholder = 'Ask a follow-up question',
  disabled = false,
}: ConversationBottomInputProps) => {
  const { selectedSDK, setSelectedSDK, setUserPrompt } = useExplorer();
  const { isGenerating, setIsTypingFollowUp } = useCodeGenerationStore();

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [inputValue, setInputValue] = useState('');

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // If input is empty, reset to initial height
    if (!inputValue.trim()) {
      textarea.style.height = '48.4px';
      return;
    }

    const resizeTextarea = () => {
      textarea.style.height = '48.4px'; // Reset to minimum first
      const maxHeight = inputValue.trim() ? 200 : 258; // Adjust max height based on whether bottom section is shown
      const newHeight = Math.min(textarea.scrollHeight, maxHeight);
      textarea.style.height = `${newHeight}px`;
    };

    resizeTextarea();
  }, [inputValue]);

  const handleTextareaChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      setInputValue(value);
    },
    []
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (isGenerating || disabled) {
        e.preventDefault();
        return;
      }

      if (
        e.key === 'Enter' &&
        !e.shiftKey &&
        !e.ctrlKey &&
        !e.altKey &&
        !e.metaKey
      ) {
        e.preventDefault();
        if (inputValue.trim()) {
          onSubmit(inputValue, selectedSDK);
          setInputValue('');
          setUserPrompt('');
        }
      }
    },
    [inputValue, selectedSDK, isGenerating, disabled, onSubmit, setUserPrompt]
  );

  const handleSendClick = useCallback(() => {
    if (!inputValue.trim() || isGenerating || disabled) return;

    onSubmit(inputValue, selectedSDK);
    setInputValue('');
    setUserPrompt('');
  }, [
    inputValue,
    selectedSDK,
    isGenerating,
    disabled,
    onSubmit,
    setUserPrompt,
  ]);

  const handleSelectSDK = useCallback(
    (value: string) => {
      setSelectedSDK(value);
    },
    [setSelectedSDK]
  );

  // Focus textarea when not disabled
  useEffect(() => {
    if (!disabled && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [disabled]);

  // Track typing state to hide "New" button
  useEffect(() => {
    setIsTypingFollowUp(inputValue.trim().length > 0);
  }, [inputValue, setIsTypingFollowUp]);

  return (
    <div className="pointer-events-none fixed bottom-0 left-0 right-0 z-[999999999999] flex items-center justify-center py-4">
      <div className="w-full max-w-[468px]">
        <div
          className="flex flex-col h-fit p-2"
          style={{ pointerEvents: 'all' }}
        >
          {/* Main glass morphism input container */}
          <div className="relative rounded-[12px] bg-white/90 shadow-lg backdrop-blur-md before:content-[''] before:absolute before:inset-0 before:rounded-[inherit] before:z-[-1] before:p-[1px] before:bg-[linear-gradient(90deg,#e93406_0%,#831d04_100%)] before:mask-custom">
            {/* Textarea wrapper */}
            <div className="relative">
              <textarea
                ref={textareaRef}
                value={inputValue}
                onChange={handleTextareaChange}
                onKeyDown={handleKeyDown}
                placeholder=""
                disabled={disabled}
                className={cn(
                  'line-height-1.5 caret-gray-800 p-3 pr-12 transition-all duration-200 ease-in-out',
                  'w-full min-h-[48.4px] select-text resize-none text-gray-900 font-medium',
                  'bg-transparent placeholder:text-gray-600 overflow-y-auto',
                  'outline-none text-[16px] scroll-p-5 selection:bg-blue-100 selection:text-gray-900',
                  'scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent',
                  inputValue.trim() ? 'max-h-[200px]' : 'max-h-[258px]',
                  disabled && 'cursor-not-allowed opacity-50',
                  // When no input, maintain original compact styling
                  !inputValue.trim() && 'rounded-[12px]'
                )}
                style={{
                  userSelect: 'text',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
                rows={1}
              />

              {/* Placeholder overlay when empty */}
              {!inputValue && (
                <div className="absolute left-3.5 top-[12.5px] pointer-events-none text-gray-500 text-[16px] transition-opacity duration-300 ease-out opacity-100">
                  {placeholder}
                </div>
              )}

              {/* Submit Button - back to original position when no text */}
              {!inputValue.trim() && (
                <button
                  onClick={handleSendClick}
                  disabled={!inputValue.trim() || isGenerating || disabled}
                  className={cn(
                    'h-6 min-h-6 w-6 min-w-6 flex items-center justify-center rounded-full',
                    'transition-all duration-100 absolute bottom-3 right-3',
                    'cursor-default opacity-30 bg-gray-400'
                  )}
                >
                  <CircleArrowUpIcon
                    className="w-4 h-4 text-white"
                    strokeWidth={2}
                  />
                </button>
              )}
            </div>

            {/* Bottom section with Submit Button and SDK Selector - only when there's input */}
            {inputValue.trim() && (
              <div className="border-t border-gray-200/20 px-3 py-2 bg-gray-50/50 rounded-b-[12px] transition-all duration-200 flex items-center justify-between">
                <SDKSelector
                  selectedSDK={selectedSDK}
                  onSelectSDK={handleSelectSDK}
                  disabled={true}
                />
                <button
                  onClick={handleSendClick}
                  disabled={!inputValue.trim() || isGenerating || disabled}
                  className={cn(
                    'h-7 min-h-7 w-7 min-w-7 flex items-center justify-center rounded-full',
                    'transition-all duration-100',
                    !disabled && !isGenerating
                      ? 'bg-gray-800 hover:bg-gray-900 hover:scale-105 cursor-pointer shadow-sm'
                      : 'cursor-default opacity-30 bg-gray-400'
                  )}
                >
                  <CircleArrowUpIcon
                    className="w-4 h-4 text-white"
                    strokeWidth={2}
                  />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversationBottomInput;
